import { useState, useCallback } from 'react'
import { useGetAccountQuery } from '../../store/accountUserSettings/accountUserSettings'
import { IntegrationExportConfirmationDialog } from './IntegrationExportConfirmationDialog'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { RoomSession } from '../../models/Room'

export type IntegrationExportConfirmationParams = {
  roomSession?: RoomSession | null
}

export const useIntegrationExportConfirmation = ({ roomSession }: IntegrationExportConfirmationParams = {}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const accountId = useAppSelector(selectAccountId)
  const [actionToConfirm, setActionToConfirm] = useState<((proceed: boolean) => void) | null>(null)
  const { data: account, isLoading: accountIsLoading } = useGetAccountQuery({ accountId })

  const confirmAction = useCallback(async (): Promise<boolean> => {
    const isConfirmationRequired = account?.data.effectiveConfig?.complianceConfig?.integrationExportConfirmationConfig?.enabled === true
    const isSessionPrivate = roomSession && roomSession.isPrivate
    if (!isConfirmationRequired && !isSessionPrivate) {
      return Promise.resolve(true)
    }

    return new Promise<boolean>((resolve) => {
      setIsDialogOpen(true)
      setActionToConfirm(() => resolve)
    })
  }, [account, roomSession])

  const handleConfirm = () => {
    actionToConfirm?.(true)
    setIsDialogOpen(false)
    setActionToConfirm(null)
  }

  const handleClose = () => {
    actionToConfirm?.(false)
    setIsDialogOpen(false)
    setActionToConfirm(null)
  }

  const runMutationWithConfirmation = useCallback(
    async (mutationTrigger: () => Promise<any>) => {
      try {
        const shouldProces = await confirmAction()
        if (shouldProces) {
          await mutationTrigger()
        }
      } catch (err) {
        throw err
      } finally {
        setIsDialogOpen(false)
        setActionToConfirm(null)
      }
    },
    [confirmAction],
  )

  const ConfirmationModal = useCallback(
    () => (
      <IntegrationExportConfirmationDialog
        isOpen={isDialogOpen}
        onClose={handleClose}
        onConfirm={handleConfirm}
        account={account?.data}
        session={roomSession}
      />
    ),
    [isDialogOpen, account],
  )

  return {
    runMutationWithConfirmation,
    accountIsLoading,
    ConfirmationModal,
  }
}
